<template>
  <div class="w-100">
    <div
      class="col-sm-12 font-weight-bold text-left d-flex justify-content-between"
    >
      <div class="d-flex align-items-center">
        <h3 class="font-weight-bold text-left mr-2">Arabidopsis paralogs</h3>
        <Tooltip
          :help-text="'List of Arabidopsis genes that reside in the same gene family as the query Arabidopsis gene.'"
          help-id="paralogs"
          label="Arabidopsis paralogs"
        />
      </div>
      <div
        v-if="content.paralogs && content.paralogs.length > 0"
        class="d-flex justify-content-right mb-2"
      >
        <b-button
          class="ml-2"
          size="md"
          @click="openNewTab({ type: 'sequences' })"
          >Get Sequences</b-button
        >
        <b-button
          class="ml-2"
          size="md"
          @click="openNewTab({ type: 'paralogs' })"
          >Download AGI IDs</b-button
        >
      </div>
    </div>
    <div class="row">
      <div v-if="!content.paralogs || content.paralogs.length == 0">
        No Paralogs Found
      </div>
      <div v-else class="col-sm-6 scrollable-container pt-2">
        <div
          v-for="(obj, j) in content.paralogs.slice(0, 2)"
          class="row w-100 ml-2"
          :key="j"
        >
          <a class="text-left" :href="obj.link" target="_blank">
            {{ obj.name }}
          </a>
        </div>
        <div v-if="content.paralogs.length > 2" class="row w-100 ml-2">
          <a href="#" @click.prevent="showMore" ref="showMoreLink"
            >Show More ...</a
          >
        </div>
      </div>
    </div>
    <hr />
    <div class="col-sm-6 pb-2 d-flex justify-content-between">
      <div class="d-flex align-items-center">
        <h3 class="font-weight-bold text-left mr-2">Plant orthologs</h3>
        <Tooltip
          :help-text="'List of plant orthologs of the query Arabidopsis gene.'"
          help-id="orthologs"
          label="Plant orthologs"
        />
      </div>

      <div class="d-flex justify-content-right mb-2">
        <b-button
          class="ml-2"
          size="md"
          @click="openNewTab({ type: 'orthologs' })"
          >Download Orthologs</b-button
        >
      </div>
    </div>
    <div class="row">
      <div class="col-sm-6">
        <h4 class="font-weight-bold text-left mb-2">
          {{ content.eudic_ortho.title }}
        </h4>
        <table
          class="table-borderless table-layout"
          role="table"
          :aria-label="content.eudic_ortho.title + ' orthologs data'"
        >
          <thead class="sr-only">
            <tr>
              <th scope="col">Plant Species</th>
              <th scope="col">Number of Orthologs</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(key, j) in Object.keys(content.eudic_ortho.mapping)"
              :key="j"
            >
              <td class="text-left pr-3">{{ key }}</td>
              <td class="text-left">
                {{ content.eudic_ortho.mapping[key] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="col-sm-6">
        <h4 class="font-weight-bold text-left mb-2">
          {{ content.mono_ortho.title }}
        </h4>
        <table
          class="table-borderless table-layout"
          role="table"
          :aria-label="content.mono_ortho.title + ' orthologs data'"
        >
          <thead class="sr-only">
            <tr>
              <th scope="col">Plant Species</th>
              <th scope="col">Number of Orthologs</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(key, j) in Object.keys(content.mono_ortho.mapping)"
              :key="j"
            >
              <td class="text-left pr-3">{{ key }}</td>
              <td class="text-left">
                {{ content.mono_ortho.mapping[key] }}
              </td>
            </tr>
          </tbody>
        </table>

        <h4 class="font-weight-bold text-left mb-2 mt-3">
          {{ content.other_ortho.title }}
        </h4>
        <table
          class="table-borderless table-layout"
          role="table"
          :aria-label="content.other_ortho.title + ' orthologs data'"
        >
          <thead class="sr-only">
            <tr>
              <th scope="col">Plant Species</th>
              <th scope="col">Number of Orthologs</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(key, j) in Object.keys(content.other_ortho.mapping)"
              :key="'ortho' + j"
            >
              <td class="text-left pr-3">{{ key }}</td>
              <td class="text-left">
                {{ content.other_ortho.mapping[key] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="showModal" class="modal-backdrop">
      <div
        class="modal-custom"
        ref="modal"
        tabindex="-1"
        @keydown.esc="closeModal"
      >
        <div class="modal-header">
          <h5>All Paralogs</h5>
          <button type="button" class="close" @click="closeModal">
            &times;
          </button>
        </div>
        <div class="modal-body">
          <div
            v-for="(obj, j) in content.paralogs"
            class="row w-100 ml-2"
            :key="j"
          >
            <a class="text-left" :href="obj.link" target="_blank">
              {{ obj.name }}
            </a>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Tooltip from "@/components/common/Tooltip.vue";

export default {
  components: {
    Tooltip,
  },
  mounted() {},
  data() {
    return {
      showModal: false,
      triggerElement: null,
    };
  },
  props: ["content", "treeId"],
  methods: {
    openNewTab(obj) {
      if (obj.type) {
        const apiBase = `${process.env.VUE_APP_PHYLOGENES_API}/api/panther`;
        if (obj.type == "orthologs") {
          window.open(`${apiBase}/orthologs-download/${this.treeId}`, "_blank");
        } else if (obj.type == "paralogs") {
          window.open(`${apiBase}/paralogs-download/${this.treeId}`, "_blank");
        }
        // else if (obj.type == "sequences") {
        //   let link = `${process.env.VUE_APP_OLD_TAIR_URL}/servlet/Search?type=gene&search_action=sendToSequenceDLByNames`;
        //   this.meta.agis.forEach((id) => {
        //     link += "&name=" + id;
        //   });
        //   window.open(link, "_blank");
        // }
      }
    },
    showMore() {
      this.triggerElement = this.$refs.showMoreLink;
      this.showModal = true;
      this.$nextTick(() => {
        this.$refs.modal.focus();
      });
    },
    closeModal() {
      this.showModal = false;
      this.$nextTick(() => {
        if (this.triggerElement) {
          this.triggerElement.focus();
        }
      });
    },
  },
};
</script>
<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.9;
}

.modal-custom {
  background: white;
  padding: 20px;
  border-radius: 5px;
  max-width: 500px;
  width: 100%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  max-height: 400px;
  overflow-y: auto;
}

.modal-footer {
  text-align: right;
}

/* Table styling to maintain the original appearance */
.table-borderless {
  border: none;
  width: 100%;
  margin-bottom: 0;
}

.table-borderless td {
  border: none;
  padding: 0;
  vertical-align: top;
  line-height: 1.5;
}

.table-layout {
  table-layout: fixed;
}

.table-layout td:first-child {
  width: 70%;
}

.table-layout td:last-child {
  width: 30%;
}

/* Screen reader only class for hidden headers */
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
